import { h } from 'preact';
import { useState } from 'preact/hooks';

import { useShopifyCurrency } from '../../../common/currency/shopify-currency.hook';
import { IconCaret } from '../../../common/icons/icon-caret';
import { buyItNow } from '../../../content/store/content.thunk';
import { useTranslation } from '../../../i18n';
import type { Product } from '../../../shopify/models/product.model';
import { useAppDispatch } from '../../../store/hooks';
import { toastService } from '../../../toast/toast.service';

type Props = {
  products: Product[];
};

export function ProductsView({ products = [] }: Props) {
  const dispatch = useAppDispatch();
  const [selectedVariantId, setSelectedVariantId] = useState<string>('');
  const { shopifyCurrency } = useShopifyCurrency();
  const { t } = useTranslation();

  const isProductSoldOut = (product: Product): boolean => {
    return !product.available;
  };

  const handleBuyItNow = async (product: Product) => {
    const productVariantId =
      product.variants.length === 1 ? product.variants[0].id : selectedVariantId;
    const checkoutWindow = window.open('', '_blank');
    const result = await dispatch(
      buyItNow({
        productVariantId,
        product,
      })
    );

    if (buyItNow.fulfilled.match(result)) {
      if (checkoutWindow) checkoutWindow.location.href = '/checkout';
    } else if (buyItNow.rejected.match(result)) {
      checkoutWindow?.close();
      console.error('[ProductCard] buyItNow failed:', result);
      toastService.error(t('common.error'), t('product.errorAddToCart'));
    }
  };

  return (
    <>
      {products.map((product, i) =>
        product ? (
          <>
            <div key={product.id} class="single-product">
              <div class="single-product-image">
                {product.featured_image && <img src={product.featured_image} alt={product.title} />}
              </div>
              <div class="single-product-inner">
                <div class="single-product-name">
                  <h3>{product.title}</h3>
                </div>
                <div
                  class={`single-product-actions ${
                    product.variants?.length > 1 ? 'single-align-stretch' : ''
                  }`}
                >
                  {product.variants?.length === 1 ? (
                    <p id={`single-product-price-${product.id}`}>
                      {shopifyCurrency(product.variants[0].price)}
                    </p>
                  ) : (
                    !isProductSoldOut(product) && (
                      <div class="select single-select-wrap single-variant-select">
                        <select
                          class="single-select select__select"
                          aria-label={product.title}
                          id={`single-variant-select-${product.id}`}
                          value={selectedVariantId}
                          onChange={(e) =>
                            setSelectedVariantId((e.target as HTMLSelectElement).value)
                          }
                        >
                          {product.options.length === 1 && (
                            <option value="" disabled selected>
                              {product.options[0]}
                            </option>
                          )}

                          {product.variants?.map((variant, varIndex) =>
                            variant.available ? (
                              <option key={variant.id} value={variant.id} selected={varIndex === 0}>
                                {variant.title} - {shopifyCurrency(variant.price)}
                              </option>
                            ) : null
                          )}
                        </select>
                        <IconCaret />
                      </div>
                    )
                  )}

                  <button
                    class="button button--secondary btn btn--secondary"
                    disabled={isProductSoldOut(product)}
                    onClick={() => handleBuyItNow(product)}
                  >
                    {!isProductSoldOut(product) ? t('product.buyNow') : t('product.soldOut')}
                  </button>
                </div>
              </div>
            </div>
            <hr class="single-hr" />
          </>
        ) : null
      )}
    </>
  );
}
